import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTPageURLToSDKPageURL } from '@wix/sdk-runtime/transformations/page-url';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ChangeCurrencyApplicationErrors,
  ChangeCurrencyOptions,
  CountAvailableTicketDefinitionsApplicationErrors,
  CountAvailableTicketDefinitionsOptions,
  CountAvailableTicketDefinitionsResponse,
  CountTicketDefinitionsApplicationErrors,
  CountTicketDefinitionsOptions,
  CountTicketDefinitionsResponse,
  CreateTicketDefinitionApplicationErrors,
  CreateTicketDefinitionOptions,
  GetTicketDefinitionOptions,
  QueryAvailableTicketDefinitionsOptions,
  QueryAvailableTicketDefinitionsResponse,
  QueryTicketDefinitionsOptions,
  QueryTicketDefinitionsResponse,
  QueryV2,
  ReorderTicketDefinitionsApplicationErrors,
  ReorderTicketDefinitionsOptions,
  TicketDefinition,
  TicketDefinitionCreatedEnvelope,
  TicketDefinitionDeletedEnvelope,
  TicketDefinitionQuery,
  TicketDefinitionSaleEndedEnvelope,
  TicketDefinitionSalePeriodUpdatedEnvelope,
  TicketDefinitionSaleStartedEnvelope,
  TicketDefinitionUpdatedEnvelope,
  TicketDefinitionsQueryBuilder,
  UpdateTicketDefinition,
  UpdateTicketDefinitionApplicationErrors,
  UpdateTicketDefinitionOptions,
  bulkDeleteTicketDefinitionsByFilter as universalBulkDeleteTicketDefinitionsByFilter,
  changeCurrency as universalChangeCurrency,
  countAvailableTicketDefinitions as universalCountAvailableTicketDefinitions,
  countTicketDefinitions as universalCountTicketDefinitions,
  createTicketDefinition as universalCreateTicketDefinition,
  deleteTicketDefinition as universalDeleteTicketDefinition,
  getTicketDefinition as universalGetTicketDefinition,
  queryAvailableTicketDefinitions as universalQueryAvailableTicketDefinitions,
  queryTicketDefinitions as universalQueryTicketDefinitions,
  reorderTicketDefinitions as universalReorderTicketDefinitions,
  typedQueryTicketDefinitions as universalTypedQueryTicketDefinitions,
  updateTicketDefinition as universalUpdateTicketDefinition,
} from './events-v3-ticket-definition-ticket-definitions-v-2.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

export function createTicketDefinition(
  httpClient: HttpClient
): CreateTicketDefinitionSignature {
  return (
    ticketDefinition: NonNullablePaths<
      TicketDefinition,
      `eventId` | `feeType` | `name` | `pricingMethod`,
      2
    >,
    options?: CreateTicketDefinitionOptions
  ) =>
    universalCreateTicketDefinition(
      ticketDefinition,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateTicketDefinitionSignature {
  /**
   * Creates a ticket definition.
   *
   * You may create up to 100 definitions per event.
   * @param - Ticket definition info.
   * @param - Currency info.
   * @returns Created ticket definition.
   */
  (
    ticketDefinition: NonNullablePaths<
      TicketDefinition,
      `eventId` | `feeType` | `name` | `pricingMethod`,
      2
    >,
    options?: CreateTicketDefinitionOptions
  ): Promise<
    NonNullablePaths<
      TicketDefinition,
      | `hidden`
      | `limited`
      | `pricingMethod.fixedPrice.value`
      | `pricingMethod.fixedPrice.currency`
      | `pricingMethod.pricingOptions.optionDetails`
      | `pricingMethod.pricingType`
      | `pricingMethod.free`
      | `feeType`
      | `salePeriod.displayNotOnSale`
      | `saleStatus`
      | `eventDetails.location.type`
      | `eventDetails.location.address.streetAddress.number`
      | `eventDetails.location.address.streetAddress.name`
      | `eventDetails.dateAndTimeSettings.recurrenceStatus`
      | `eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates`
      | `eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.${number}.showTimeZone`
      | `eventDetails.status`
      | `seatingDetails.places`
      | `seatingDetails.places.${number}.placeId`
      | `seatingDetails.places.${number}.label`
      | `seatingDetails.places.${number}.availableCapacity`,
      7
    > & {
      __applicationErrorsType?: CreateTicketDefinitionApplicationErrors;
    }
  >;
}

export function updateTicketDefinition(
  httpClient: HttpClient
): UpdateTicketDefinitionSignature {
  return (
    _id: string,
    ticketDefinition: NonNullablePaths<UpdateTicketDefinition, `revision`, 2>,
    options?: UpdateTicketDefinitionOptions
  ) =>
    universalUpdateTicketDefinition(
      _id,
      ticketDefinition,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateTicketDefinitionSignature {
  /**
   * Updates a ticket definition.
   *
   * Each time the ticket definition is updated, `revision` increments by 1. The existing `revision` must be included when updating the ticket definition. This ensures you're working with the latest ticket definition and prevents unintended overwrites.
   * @param - Ticket definition ID.
   * @param - Optional fields.
   * @param - Ticket definition to update.
   * @returns The updated ticket definition.
   */
  (
    _id: string,
    ticketDefinition: NonNullablePaths<UpdateTicketDefinition, `revision`, 2>,
    options?: UpdateTicketDefinitionOptions
  ): Promise<
    NonNullablePaths<
      TicketDefinition,
      | `hidden`
      | `limited`
      | `pricingMethod.fixedPrice.value`
      | `pricingMethod.fixedPrice.currency`
      | `pricingMethod.pricingOptions.optionDetails`
      | `pricingMethod.pricingType`
      | `pricingMethod.free`
      | `feeType`
      | `salePeriod.displayNotOnSale`
      | `saleStatus`
      | `eventDetails.location.type`
      | `eventDetails.location.address.streetAddress.number`
      | `eventDetails.location.address.streetAddress.name`
      | `eventDetails.dateAndTimeSettings.recurrenceStatus`
      | `eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates`
      | `eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.${number}.showTimeZone`
      | `eventDetails.status`
      | `seatingDetails.places`
      | `seatingDetails.places.${number}.placeId`
      | `seatingDetails.places.${number}.label`
      | `seatingDetails.places.${number}.availableCapacity`,
      7
    > & {
      __applicationErrorsType?: UpdateTicketDefinitionApplicationErrors;
    }
  >;
}

export function getTicketDefinition(
  httpClient: HttpClient
): GetTicketDefinitionSignature {
  return (ticketDefinitionId: string, options?: GetTicketDefinitionOptions) =>
    universalGetTicketDefinition(
      ticketDefinitionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTicketDefinitionSignature {
  /**
   * Retrieves a ticket definition.
   * @param - Ticket definition ID.
   * @param - Optional fields.
   * @returns The requested ticket definition.
   */
  (ticketDefinitionId: string, options?: GetTicketDefinitionOptions): Promise<
    NonNullablePaths<
      TicketDefinition,
      | `hidden`
      | `limited`
      | `pricingMethod.fixedPrice.value`
      | `pricingMethod.fixedPrice.currency`
      | `pricingMethod.pricingOptions.optionDetails`
      | `pricingMethod.pricingType`
      | `pricingMethod.free`
      | `feeType`
      | `salePeriod.displayNotOnSale`
      | `saleStatus`
      | `eventDetails.location.type`
      | `eventDetails.location.address.streetAddress.number`
      | `eventDetails.location.address.streetAddress.name`
      | `eventDetails.dateAndTimeSettings.recurrenceStatus`
      | `eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates`
      | `eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.${number}.showTimeZone`
      | `eventDetails.status`
      | `seatingDetails.places`
      | `seatingDetails.places.${number}.placeId`
      | `seatingDetails.places.${number}.label`
      | `seatingDetails.places.${number}.availableCapacity`,
      7
    >
  >;
}

export function deleteTicketDefinition(
  httpClient: HttpClient
): DeleteTicketDefinitionSignature {
  return (ticketDefinitionId: string) =>
    universalDeleteTicketDefinition(
      ticketDefinitionId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteTicketDefinitionSignature {
  /**
   * Deletes a ticket definition.
   * @param - ID of the ticket definition to delete.
   */
  (ticketDefinitionId: string): Promise<void>;
}

export function reorderTicketDefinitions(
  httpClient: HttpClient
): ReorderTicketDefinitionsSignature {
  return (
    eventId: string,
    options?: NonNullablePaths<
      ReorderTicketDefinitionsOptions,
      `ticketDefinitionId`,
      2
    >
  ) =>
    universalReorderTicketDefinitions(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ReorderTicketDefinitionsSignature {
  /**
   * Adjusts the order in which ticket definitions display in an event dashboard, as well as the order of available tickets displayed in the ticket picker.
   * > **Note:** You may use both `beforeTicketDefinitionId` and `afterTicketDefinitionId` but only the last one provided takes effect.
   * @param - Event ID.
   * @param - Optional fields.
   */
  (
    eventId: string,
    options?: NonNullablePaths<
      ReorderTicketDefinitionsOptions,
      `ticketDefinitionId`,
      2
    >
  ): Promise<
    void & {
      __applicationErrorsType?: ReorderTicketDefinitionsApplicationErrors;
    }
  >;
}

export function queryTicketDefinitions(
  httpClient: HttpClient
): QueryTicketDefinitionsSignature {
  return (options?: QueryTicketDefinitionsOptions) =>
    universalQueryTicketDefinitions(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryTicketDefinitionsSignature {
  /**
   * Retrieves a list of ticket definitions, given the provided paging, filtering, and sorting.
   * Query Ticket Definitions runs with these defaults, which you can override:
   * - `createdDate` is sorted in `ASC` order
   * - `paging.limit` is `100`
   * - `paging.offset` is `0`
   * @param - Optional fields.
   */
  (options?: QueryTicketDefinitionsOptions): TicketDefinitionsQueryBuilder;
}

export function typedQueryTicketDefinitions(
  httpClient: HttpClient
): TypedQueryTicketDefinitionsSignature {
  return (
    query: TicketDefinitionQuery,
    options?: QueryTicketDefinitionsOptions
  ) =>
    universalTypedQueryTicketDefinitions(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryTicketDefinitionsSignature {
  /** */
  (
    query: TicketDefinitionQuery,
    options?: QueryTicketDefinitionsOptions
  ): Promise<
    NonNullablePaths<
      QueryTicketDefinitionsResponse,
      | `ticketDefinitions`
      | `ticketDefinitions.${number}.hidden`
      | `ticketDefinitions.${number}.limited`
      | `ticketDefinitions.${number}.pricingMethod.fixedPrice.value`
      | `ticketDefinitions.${number}.pricingMethod.fixedPrice.currency`
      | `ticketDefinitions.${number}.pricingMethod.pricingType`
      | `ticketDefinitions.${number}.pricingMethod.free`
      | `ticketDefinitions.${number}.feeType`
      | `ticketDefinitions.${number}.salePeriod.displayNotOnSale`
      | `ticketDefinitions.${number}.saleStatus`
      | `ticketDefinitions.${number}.eventDetails.location.type`
      | `ticketDefinitions.${number}.eventDetails.dateAndTimeSettings.recurrenceStatus`
      | `ticketDefinitions.${number}.eventDetails.status`,
      6
    >
  >;
}

export function queryAvailableTicketDefinitions(
  httpClient: HttpClient
): QueryAvailableTicketDefinitionsSignature {
  return (query: QueryV2, options?: QueryAvailableTicketDefinitionsOptions) =>
    universalQueryAvailableTicketDefinitions(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryAvailableTicketDefinitionsSignature {
  /**
   * Retrieves a list of available ticket definitions with the specified paging, filtering, and sorting.
   *
   * This method retrieves ticket definitions that aren't in the `hidden` state. The available ticket definitions can be retrieved by site visitors.
   *
   * > **Note:** You need the `WIX_EVENTS.READ_TICKET_DEFINITIONS` permission to get the `salesDetails` field values.
   *
   * Query Available Ticket Definitions runs with these defaults, which you can override:
   * - `createdDate` is sorted in `ASC` order
   * - `paging.limit` is `100`
   * - `paging.offset` is `0`
   *
   * To learn about working with _Query_ methods, see [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language).
   * @param - Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details.
   * @param - Optional fields.
   */
  (query: QueryV2, options?: QueryAvailableTicketDefinitionsOptions): Promise<
    NonNullablePaths<
      QueryAvailableTicketDefinitionsResponse,
      | `ticketDefinitions`
      | `ticketDefinitions.${number}.hidden`
      | `ticketDefinitions.${number}.limited`
      | `ticketDefinitions.${number}.pricingMethod.fixedPrice.value`
      | `ticketDefinitions.${number}.pricingMethod.fixedPrice.currency`
      | `ticketDefinitions.${number}.pricingMethod.pricingType`
      | `ticketDefinitions.${number}.pricingMethod.free`
      | `ticketDefinitions.${number}.feeType`
      | `ticketDefinitions.${number}.salePeriod.displayNotOnSale`
      | `ticketDefinitions.${number}.saleStatus`
      | `ticketDefinitions.${number}.eventDetails.location.type`
      | `ticketDefinitions.${number}.eventDetails.dateAndTimeSettings.recurrenceStatus`
      | `ticketDefinitions.${number}.eventDetails.status`,
      6
    >
  >;
}

export function countTicketDefinitions(
  httpClient: HttpClient
): CountTicketDefinitionsSignature {
  return (options?: CountTicketDefinitionsOptions) =>
    universalCountTicketDefinitions(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountTicketDefinitionsSignature {
  /**
   * Counts ticket definitions by the `saleStatus` field, including those with the `hidden` status.
   * @param - Optional fields.
   */
  (options?: CountTicketDefinitionsOptions): Promise<
    CountTicketDefinitionsResponse & {
      __applicationErrorsType?: CountTicketDefinitionsApplicationErrors;
    }
  >;
}

export function countAvailableTicketDefinitions(
  httpClient: HttpClient
): CountAvailableTicketDefinitionsSignature {
  return (options?: CountAvailableTicketDefinitionsOptions) =>
    universalCountAvailableTicketDefinitions(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountAvailableTicketDefinitionsSignature {
  /**
   * Counts ticket definitions by the `saleStatus`, excluding those with the `hidden` status.
   * @param - Optional fields.
   */
  (options?: CountAvailableTicketDefinitionsOptions): Promise<
    CountAvailableTicketDefinitionsResponse & {
      __applicationErrorsType?: CountAvailableTicketDefinitionsApplicationErrors;
    }
  >;
}

export function bulkDeleteTicketDefinitionsByFilter(
  httpClient: HttpClient
): BulkDeleteTicketDefinitionsByFilterSignature {
  return (filter: Record<string, any>) =>
    universalBulkDeleteTicketDefinitionsByFilter(
      filter,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteTicketDefinitionsByFilterSignature {
  /**
   * Deletes multiple ticket definitions.
   *
   * All ticket definitions that meet the specified `filter` criteria are deleted.
   * @param - Filter object. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details.
   */
  (filter: Record<string, any>): Promise<void>;
}

export function changeCurrency(
  httpClient: HttpClient
): ChangeCurrencySignature {
  return (
    eventId: string,
    options: NonNullablePaths<ChangeCurrencyOptions, `currency`, 2>
  ) =>
    universalChangeCurrency(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ChangeCurrencySignature {
  /**
   * Changes ticket price currency per event.
   *
   * Updates the currency for all ticket definitions under the event.
   * This doesn't convert price values. It only updates the currency code
   * (for example, 10 USD becomes 10 EUR).
   * @param - Event ID.
   * @param - Optional fields.
   */
  (
    eventId: string,
    options: NonNullablePaths<ChangeCurrencyOptions, `currency`, 2>
  ): Promise<
    void & {
      __applicationErrorsType?: ChangeCurrencyApplicationErrors;
    }
  >;
}

export const onTicketDefinitionCreated = EventDefinition(
  'wix.events.v3.ticket_definition_created',
  true,
  (event: TicketDefinitionCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.eventCreatedDate' },
            { path: 'entity.salePeriod.startDate' },
            { path: 'entity.salePeriod.endDate' },
            { path: 'entity.eventDetails.dateAndTimeSettings.startDate' },
            { path: 'entity.eventDetails.dateAndTimeSettings.endDate' },
            {
              path: 'entity.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'entity.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'entity.eventDetails.eventPageUrl' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'entity.eventDetails.location.address' }],
        },
      ])
    )
)<TicketDefinitionCreatedEnvelope>();
export const onTicketDefinitionDeleted = EventDefinition(
  'wix.events.v3.ticket_definition_deleted',
  true,
  (event: TicketDefinitionDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.eventCreatedDate' },
            { path: 'entity.salePeriod.startDate' },
            { path: 'entity.salePeriod.endDate' },
            { path: 'entity.eventDetails.dateAndTimeSettings.startDate' },
            { path: 'entity.eventDetails.dateAndTimeSettings.endDate' },
            {
              path: 'entity.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'entity.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'entity.eventDetails.eventPageUrl' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'entity.eventDetails.location.address' }],
        },
      ])
    )
)<TicketDefinitionDeletedEnvelope>();
export const onTicketDefinitionSaleEnded = EventDefinition(
  'wix.events.v3.ticket_definition_sale_ended',
  true,
  (event: TicketDefinitionSaleEndedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.ticketDefinition.createdDate' },
            { path: 'data.ticketDefinition.updatedDate' },
            { path: 'data.ticketDefinition.eventCreatedDate' },
            { path: 'data.ticketDefinition.salePeriod.startDate' },
            { path: 'data.ticketDefinition.salePeriod.endDate' },
            {
              path: 'data.ticketDefinition.eventDetails.dateAndTimeSettings.startDate',
            },
            {
              path: 'data.ticketDefinition.eventDetails.dateAndTimeSettings.endDate',
            },
            {
              path: 'data.ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'data.ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'data.ticketDefinition.eventDetails.eventPageUrl' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.ticketDefinition.eventDetails.location.address' },
          ],
        },
      ])
    )
)<TicketDefinitionSaleEndedEnvelope>();
export const onTicketDefinitionSalePeriodUpdated = EventDefinition(
  'wix.events.v3.ticket_definition_sale_period_updated',
  true,
  (event: TicketDefinitionSalePeriodUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.afterUpdate.startDate' },
            { path: 'data.afterUpdate.endDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<TicketDefinitionSalePeriodUpdatedEnvelope>();
export const onTicketDefinitionSaleStarted = EventDefinition(
  'wix.events.v3.ticket_definition_sale_started',
  true,
  (event: TicketDefinitionSaleStartedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.ticketDefinition.createdDate' },
            { path: 'data.ticketDefinition.updatedDate' },
            { path: 'data.ticketDefinition.eventCreatedDate' },
            { path: 'data.ticketDefinition.salePeriod.startDate' },
            { path: 'data.ticketDefinition.salePeriod.endDate' },
            {
              path: 'data.ticketDefinition.eventDetails.dateAndTimeSettings.startDate',
            },
            {
              path: 'data.ticketDefinition.eventDetails.dateAndTimeSettings.endDate',
            },
            {
              path: 'data.ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'data.ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'data.ticketDefinition.eventDetails.eventPageUrl' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.ticketDefinition.eventDetails.location.address' },
          ],
        },
      ])
    )
)<TicketDefinitionSaleStartedEnvelope>();
export const onTicketDefinitionUpdated = EventDefinition(
  'wix.events.v3.ticket_definition_updated',
  true,
  (event: TicketDefinitionUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.eventCreatedDate' },
            { path: 'entity.salePeriod.startDate' },
            { path: 'entity.salePeriod.endDate' },
            { path: 'entity.eventDetails.dateAndTimeSettings.startDate' },
            { path: 'entity.eventDetails.dateAndTimeSettings.endDate' },
            {
              path: 'entity.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'entity.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.eventCreatedDate' },
            { path: 'modifiedFields.salePeriod.startDate' },
            { path: 'modifiedFields.salePeriod.endDate' },
            {
              path: 'modifiedFields.eventDetails.dateAndTimeSettings.startDate',
            },
            { path: 'modifiedFields.eventDetails.dateAndTimeSettings.endDate' },
            {
              path: 'modifiedFields.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'modifiedFields.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'entity.eventDetails.eventPageUrl' },
            { path: 'modifiedFields.eventDetails.eventPageUrl' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.eventDetails.location.address' },
            { path: 'modifiedFields.eventDetails.location.address' },
          ],
        },
      ])
    )
)<TicketDefinitionUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  Agenda,
  Alignment,
  AnchorData,
  App,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  AspectRatio,
  Asset,
  AudioData,
  AvailablePlace,
  Backdrop,
  BackdropType,
  Background,
  BackgroundGradient,
  BackgroundImage,
  BackgroundType,
  Badge,
  Banner,
  BannerPosition,
  BaseEventMetadata,
  BlockquoteData,
  BookingData,
  Border,
  BorderColors,
  BorderWidths,
  BulkCopyTicketDefinitionsByEventIdRequest,
  BulkCopyTicketDefinitionsByEventIdResponse,
  BulkDeleteTicketDefinitionsByFilterRequest,
  BulkDeleteTicketDefinitionsByFilterResponse,
  BulletedListData,
  ButtonData,
  ButtonDataType,
  ButtonStyles,
  CalendarLinks,
  CancellationActor,
  CancellationActorType,
  CancellationInfo,
  CaptionData,
  CardData,
  CardDataBackground,
  CardDataBackgroundType,
  CardStyles,
  CardStylesType,
  Category,
  CategoryCounts,
  CategoryDetails,
  CategoryStateState,
  CellStyle,
  ChangeCurrencyOptions,
  ChangeCurrencyRequest,
  ChangeCurrencyResponse,
  CheckboxListData,
  CheckoutFormMessages,
  CheckoutType,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  Colors,
  ColumnSize,
  CommonAddress,
  CommonAddressLocation,
  CommonAddressStreetOneOf,
  CommonMoney,
  CommonStreetAddress,
  CommonSubdivision,
  ConferenceType,
  CopiedTicketDefinition,
  CountAvailableTicketDefinitionsOptions,
  CountAvailableTicketDefinitionsRequest,
  CountAvailableTicketDefinitionsResponse,
  CountTicketDefinitionsOptions,
  CountTicketDefinitionsRequest,
  CountTicketDefinitionsResponse,
  CreateTicketDefinitionOptions,
  CreateTicketDefinitionRequest,
  CreateTicketDefinitionResponse,
  Crop,
  CursorPaging,
  Cursors,
  CustomTag,
  Dashboard,
  DateAndTimeSettings,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  DeleteContext,
  DeleteStatus,
  DeleteTicketDefinitionRequest,
  DeleteTicketDefinitionResponse,
  Design,
  DesignTarget,
  Dimensions,
  Direction,
  DividerData,
  DividerDataAlignment,
  DocumentStyle,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  EmbedData,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Event,
  EventCanceled,
  EventCreated,
  EventData,
  EventDeleted,
  EventDetails,
  EventDisplaySettings,
  EventEnded,
  EventMetadata,
  EventStatus,
  EventTicketingSummary,
  EventType,
  EventUpdated,
  EventsLocation,
  EventsOccurrence,
  EventsRecurrenceStatusStatus,
  EventsRecurrences,
  ExtendedFields,
  ExternalEvent,
  FacetCounts,
  FeeTypeEnumType,
  Feed,
  Field,
  File,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FontFamilyData,
  FontSizeData,
  FontType,
  Form,
  FormMessages,
  Formatted,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  GalleryOptionsLayout,
  GetTicketDefinitionFromTrashBinRequest,
  GetTicketDefinitionFromTrashBinResponse,
  GetTicketDefinitionOptions,
  GetTicketDefinitionRequest,
  GetTicketDefinitionResponse,
  GetTicketDefinitionSummaryRequest,
  GetTicketDefinitionSummaryResponse,
  Gradient,
  GradientType,
  GuestListConfig,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageData,
  ImageDataCrop,
  ImageDataStyles,
  ImagePosition,
  ImagePositionPosition,
  ImageScalingScaling,
  ImageStyles,
  Indentation,
  InitialExpandedItems,
  Input,
  InputControl,
  InputControlType,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  Item,
  ItemDataOneOf,
  ItemStyle,
  Keyword,
  Label,
  LabellingSettings,
  Layout,
  LayoutCellData,
  LayoutData,
  LayoutDataBackground,
  LayoutDataBackgroundImage,
  LayoutDataBackgroundType,
  LayoutType,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  ListEventTicketingSummaryRequest,
  ListEventTicketingSummaryResponse,
  ListItemNodeData,
  ListStyle,
  ListValue,
  Location,
  LocationLocationType,
  LocationType,
  MapCoordinates,
  MapData,
  MapSettings,
  MapType,
  Media,
  MentionData,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Metadata,
  Money,
  Namespace,
  NamespaceChanged,
  Negative,
  NegativeResponseConfirmation,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NullValue,
  Occurrence,
  OdeditorAssigned,
  OdeditorUnassigned,
  Oembed,
  OnlineConferencing,
  OnlineConferencingConfig,
  OnlineConferencingSession,
  Option,
  OptionDesign,
  OptionDetails,
  OptionLayout,
  OptionSelection,
  OptionSelectionSelectedOptionOneOf,
  OrderedListData,
  Orientation,
  Origin,
  PDFSettings,
  Page,
  Pages,
  Paging,
  PagingMetadataV2,
  ParagraphData,
  Permissions,
  PicassoAssigned,
  PicassoUnassigned,
  Placement,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollDesignBackground,
  PollDesignBackgroundBackgroundOneOf,
  PollDesignBackgroundType,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  PollSettings,
  Position,
  Positive,
  PositiveResponseConfirmation,
  PricingData,
  PricingMethod,
  PricingMethodPriceOneOf,
  PricingOptions,
  PricingTypeEnumType,
  QueryAvailableTicketDefinitionsOptions,
  QueryAvailableTicketDefinitionsRequest,
  QueryAvailableTicketDefinitionsResponse,
  QueryTicketDefinitionsOptions,
  QueryTicketDefinitionsRequest,
  QueryTicketDefinitionsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RecurrenceStatusStatus,
  Recurrences,
  Registration,
  RegistrationClosedMessages,
  RegistrationStatus,
  Rel,
  ReorderTicketDefinitionsOptions,
  ReorderTicketDefinitionsOptionsReferenceDefinitionOneOf,
  ReorderTicketDefinitionsRequest,
  ReorderTicketDefinitionsRequestReferenceDefinitionOneOf,
  ReorderTicketDefinitionsResponse,
  Resizing,
  ResponseConfirmation,
  ResponsivenessBehaviour,
  RestoreInfo,
  RibbonStyles,
  RichContent,
  RsvpCollection,
  RsvpCollectionConfig,
  RsvpConfirmationMessages,
  RsvpConfirmationMessagesNegativeResponseConfirmation,
  RsvpConfirmationMessagesPositiveResponseConfirmation,
  RsvpFormMessages,
  RsvpStatusOptions,
  RsvpSummary,
  SalePeriod,
  SalePeriodUpdated,
  SaleStatusEnumStatus,
  SalesDetails,
  Scaling,
  ScheduleConfig,
  Scheduling,
  SeatingDetails,
  SeatingPlanCategoriesSummaryUpdated,
  SeoSchema,
  SeoSettings,
  ServiceProvisioned,
  ServiceRemoved,
  Settings,
  ShapeData,
  ShapeDataStyles,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrl,
  SiteUrlChanged,
  SketchData,
  SmartBlockCellData,
  SmartBlockData,
  SmartBlockDataType,
  SortOrder,
  Sorting,
  Source,
  Spoiler,
  SpoilerData,
  State,
  Status,
  Stop,
  StreetAddress,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  Styles,
  StylesBorder,
  StylesPosition,
  Subdivision,
  SubdivisionSubdivisionType,
  SubdivisionType,
  TableCellData,
  TableData,
  Tag,
  Target,
  TaxConfig,
  TaxType,
  TextAlignment,
  TextData,
  TextNodeStyle,
  TextStyle,
  Thumbnails,
  ThumbnailsAlignment,
  TicketDefinition,
  TicketDefinitionCreatedEnvelope,
  TicketDefinitionDeletedEnvelope,
  TicketDefinitionQuerySpec,
  TicketDefinitionSaleEnded,
  TicketDefinitionSaleEndedEnvelope,
  TicketDefinitionSalePeriodUpdatedEnvelope,
  TicketDefinitionSaleStarted,
  TicketDefinitionSaleStartedEnvelope,
  TicketDefinitionSummary,
  TicketDefinitionUpdatedEnvelope,
  TicketDefinitionsQueryBuilder,
  TicketDefinitionsQueryResult,
  Ticketing,
  TicketingConfig,
  TicketingSummary,
  TicketsConfirmationMessages,
  TicketsUnavailableMessages,
  TocData,
  Type,
  URI,
  URIs,
  UpdateEventTicketingSummaryRequest,
  UpdateEventTicketingSummaryResponse,
  UpdateFeeTypesBasedOnSettingsRequest,
  UpdateFeeTypesBasedOnSettingsResponse,
  UpdateTicketDefinition,
  UpdateTicketDefinitionOptions,
  UpdateTicketDefinitionRequest,
  UpdateTicketDefinitionResponse,
  UpdateTicketDefinitionSortIndexRequest,
  UpdateTicketDefinitionSortIndexResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  ValueType,
  Variant,
  VerticalAlignment,
  VerticalAlignmentAlignment,
  Video,
  VideoData,
  ViewMode,
  ViewRole,
  VisitorType,
  VoteRole,
  WebhookIdentityType,
  Width,
  WidthType,
  WixelAssigned,
  WixelUnassigned,
  utils,
} from './events-v3-ticket-definition-ticket-definitions-v-2.universal.js';
